import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Globe2,
  Layers,
  TrendingUp,
  ShieldCheck,
  FileSearch,
  Compass,
  Target,
  Activity,
  LineChart as LineIcon,
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useI18n } from '@/context/LanguageContext';
import { SectionReveal } from '@/components/SectionReveal';
import { MarketDashboard } from '@/components/MarketDashboard';
import { ContactForm } from '@/components/ContactForm';
import { scrollToId } from '@/lib/scroll';
import IMAGES from '@/content/images';

const BENEFIT_ICONS = [FileSearch, Globe2, Layers, TrendingUp];
const STEP_ICONS = [Compass, Target, Activity, LineIcon];
const spark = [{ v: 8 }, { v: 12 }, { v: 10 }, { v: 15 }, { v: 14 }, { v: 19 }, { v: 22 }];

function Kicker({ children }) {
  return (
    <span className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-[hsl(var(--inv-terracotta))]">
      {children}
    </span>
  );
}

export default function Home() {
  const { t } = useI18n();
  const location = useLocation();

  // Smooth-scroll to a section when arriving with a hash (e.g. from a legal page).
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const timer = setTimeout(() => scrollToId(id), 60);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const heroKpis = [
    { label: t('hero.kpi.lithium'), unit: t('hero.kpi.lithiumUnit'), value: '14.850', delta: '+3,4%' },
    { label: t('hero.kpi.oil'), unit: t('hero.kpi.oilUnit'), value: '68,20', delta: '+1,1%' },
    { label: t('hero.kpi.gas'), unit: t('hero.kpi.gasUnit'), value: '3,85', delta: '-0,6%' },
  ];

  const benefits = t('benefits.items');
  const steps = t('how.steps');
  const transparencyCards = t('transparency.cards');
  const tTable = t('transparency.table');

  return (
    <div className="inv-content">
      {/* HERO */}
      <section id="inicio" className="relative scroll-mt-24 overflow-hidden border-b border-border/70">
        <div className="inv-grid absolute inset-0 opacity-[0.5]" aria-hidden="true" />
        <div className="inv-hero-glow absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[1120px] grid-cols-1 gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:py-24 lg:px-8">
          <div className="lg:col-span-6">
            <Kicker>{t('hero.kicker')}</Kicker>
            <h1 className="mt-4 font-heading text-4xl font-semibold leading-[1.06] tracking-tight text-[hsl(var(--inv-slate))] sm:text-5xl">
              {t('hero.headline')}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
              {t('hero.intro1')}
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
              {t('hero.intro2')}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                onClick={() => scrollToId('datos')}
                data-testid="hero-cta-primary"
                className="bg-[hsl(var(--inv-blue))] text-[hsl(var(--primary-foreground))] transition-colors hover:bg-[hsl(var(--inv-blue-2))] active:scale-[0.99]"
              >
                {t('hero.ctaPrimary')}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToId('contacto')}
                data-testid="hero-cta-secondary"
                className="border-[hsl(var(--inv-terracotta))] text-[hsl(var(--inv-terracotta))] transition-colors hover:bg-[hsl(var(--inv-terracotta-soft))]"
              >
                {t('hero.ctaSecondary')}
              </Button>
            </div>
          </div>

          {/* Right analytics panel */}
          <div className="lg:col-span-6">
            <Card className="overflow-hidden border-border/70 shadow-[var(--shadow-md)]">
              <div className="flex items-center justify-between border-b border-border/70 bg-[hsl(var(--inv-surface))] px-5 py-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-[hsl(var(--inv-blue))]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                    {t('hero.panelKicker')}
                  </span>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground">{t('hero.panelUpdated')}</span>
              </div>
              <div className="p-5">
                <div className="space-y-2">
                  {heroKpis.map((k, i) => {
                    const neg = k.delta.startsWith('-');
                    return (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                        <div>
                          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{k.label}</p>
                          <p className="mt-0.5 font-mono text-lg font-medium text-[hsl(var(--inv-slate))]">
                            {k.value} <span className="text-xs text-muted-foreground">{k.unit}</span>
                          </p>
                        </div>
                        <span className={`font-mono text-xs font-medium ${neg ? 'text-destructive' : 'text-[hsl(188,45%,30%)]'}`}>{k.delta}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 rounded-lg border border-border/60 p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{t('hero.projectsTitle')}</span>
                    <div className="h-10 w-24">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="sp" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="hsl(18 62% 46%)" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="hsl(18 62% 46%)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="v" stroke="hsl(18 62% 46%)" strokeWidth={2} fill="url(#sp)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {['SALTA', 'JUJUY', 'NEUQUÉN', 'VACA MUERTA', 'CATAMARCA'].map((c) => (
                      <Badge key={c} variant="secondary" className="font-mono text-[10px]">{c}</Badge>
                    ))}
                  </div>
                </div>

                <figure className="mt-4">
                  <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-lg border border-border/60">
                    <img src={IMAGES.heroMine} alt={t('hero.photoCaption')} loading="eager" className="h-full w-full object-cover" />
                  </AspectRatio>
                  <figcaption className="mt-2 font-mono text-[11px] text-muted-foreground">{t('hero.photoCaption')}</figcaption>
                </figure>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="scroll-mt-24 bg-background">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
          <SectionReveal>
            <Kicker>{t('benefits.kicker')}</Kicker>
            <h2 className="mt-3 max-w-3xl font-heading text-2xl font-semibold tracking-tight text-[hsl(var(--inv-slate))] sm:text-3xl">
              {t('benefits.title')}
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">{t('benefits.intro')}</p>
          </SectionReveal>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            {Array.isArray(benefits) &&
              benefits.map((b, i) => {
                const Icon = BENEFIT_ICONS[i] || Layers;
                return (
                  <SectionReveal as="div" delay={i * 0.05} key={i}>
                    <Card className="group h-full border-border/70 p-6 transition-[box-shadow,border-color] duration-200 hover:border-[hsl(var(--inv-blue))]/40 hover:shadow-[var(--shadow-sm)]">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[hsl(var(--inv-terracotta-soft))]">
                        <Icon className="h-5 w-5 text-[hsl(var(--inv-terracotta))]" />
                      </span>
                      <h3 className="mt-4 font-heading text-lg font-semibold text-[hsl(var(--inv-slate))]">{b.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
                    </Card>
                  </SectionReveal>
                );
              })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="metodologia" className="scroll-mt-24 border-y border-border/70 bg-[hsl(var(--inv-surface))]">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
          <SectionReveal>
            <Kicker>{t('how.kicker')}</Kicker>
            <h2 className="mt-3 max-w-3xl font-heading text-2xl font-semibold tracking-tight text-[hsl(var(--inv-slate))] sm:text-3xl">
              {t('how.title')}
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">{t('how.intro')}</p>
          </SectionReveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(steps) &&
              steps.map((s, i) => {
                const Icon = STEP_ICONS[i] || Compass;
                return (
                  <SectionReveal as="div" delay={i * 0.05} key={i}>
                    <Card className="group relative h-full border-border/70 bg-background p-6 transition-[box-shadow,border-color] duration-200 hover:border-[hsl(var(--inv-blue))]/40 hover:shadow-[var(--shadow-sm)]">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-2xl font-medium text-[hsl(var(--inv-blue))]/25">{s.num}</span>
                        <Icon className="h-5 w-5 text-[hsl(var(--inv-terracotta))] transition-transform duration-200 group-hover:translate-x-0.5" />
                      </div>
                      <h3 className="mt-4 font-heading text-base font-semibold text-[hsl(var(--inv-slate))]">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                    </Card>
                  </SectionReveal>
                );
              })}
          </div>
        </div>
      </section>

      {/* MARKET DATA */}
      <section id="datos" className="scroll-mt-24 bg-background">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
          <SectionReveal>
            <Kicker>{t('market.kicker')}</Kicker>
            <h2 className="mt-3 max-w-3xl font-heading text-2xl font-semibold tracking-tight text-[hsl(var(--inv-slate))] sm:text-3xl">
              {t('market.title')}
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">{t('market.intro')}</p>
          </SectionReveal>
          <div className="mt-10">
            <MarketDashboard />
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { src: IMAGES.refinery, cap: 'Vaca Muerta — Neuquén' },
              { src: IMAGES.miningTrucks, cap: 'Minería — Catamarca' },
              { src: IMAGES.solarField, cap: 'Transición energética' },
            ].map((im, i) => (
              <figure key={i} className="overflow-hidden rounded-lg border border-border/70">
                <AspectRatio ratio={4 / 3}>
                  <img src={im.src} alt={im.cap} loading="lazy" className="h-full w-full object-cover" />
                </AspectRatio>
                <figcaption className="bg-[hsl(var(--inv-surface))] px-3 py-2 font-mono text-[11px] text-muted-foreground">{im.cap}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparencia" className="scroll-mt-24 border-y border-border/70 bg-[hsl(var(--inv-surface))]">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <SectionReveal as="div" className="lg:col-span-6">
              <Kicker>{t('transparency.kicker')}</Kicker>
              <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-[hsl(var(--inv-slate))] sm:text-3xl">
                {t('transparency.title')}
              </h2>
              <p className="mt-4 text-muted-foreground">{t('transparency.p1')}</p>
              <p className="mt-4 text-muted-foreground">{t('transparency.p2')}</p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {Array.isArray(transparencyCards) &&
                  transparencyCards.map((c, i) => (
                    <Card key={i} className="border-border/70 bg-background p-5">
                      <ShieldCheck className="h-5 w-5 text-[hsl(var(--inv-blue))]" />
                      <h3 className="mt-3 font-heading text-sm font-semibold text-[hsl(var(--inv-slate))]">{c.title}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground">{c.text}</p>
                    </Card>
                  ))}
              </div>
            </SectionReveal>

            <SectionReveal as="div" delay={0.08} className="lg:col-span-6">
              <figure className="overflow-hidden rounded-xl border border-border/70 bg-background">
                <AspectRatio ratio={16 / 10}>
                  <img src={IMAGES.dashboardDark} alt={t('transparency.photoCaption')} loading="lazy" className="h-full w-full object-cover" />
                </AspectRatio>
                <figcaption className="px-4 py-2 font-mono text-[11px] text-muted-foreground">{t('transparency.photoCaption')}</figcaption>
              </figure>
              <Card className="mt-4 border-border/70 bg-background p-4">
                <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{tTable.title}</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Array.isArray(tTable.cols) && tTable.cols.map((c, i) => (
                        <TableHead key={i} className="font-mono text-[11px] uppercase tracking-wide">{c}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.isArray(tTable.rows) && tTable.rows.map((r, ri) => (
                      <TableRow key={ri}>
                        {r.map((cell, ci) => (
                          <TableCell key={ci} className={ci === 0 ? 'font-medium text-[hsl(var(--inv-slate))]' : 'text-muted-foreground'}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="scroll-mt-24 bg-background">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Kicker>{t('contact.kicker')}</Kicker>
              <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-[hsl(var(--inv-slate))] sm:text-3xl">
                {t('contact.title')}
              </h2>
              <p className="mt-3 text-muted-foreground">{t('contact.subtitle')}</p>
              <div className="mt-6 space-y-3 text-sm">
                <p><span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t('contact.emailLabel')}</span><br /><a href={`mailto:${t('contact.email')}`} data-testid="contact-email-link" className="text-[hsl(var(--inv-blue))] hover:underline">{t('contact.email')}</a></p>
                <p><span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t('contact.phoneLabel')}</span><br /><a href={`tel:${t('contact.phone').replace(/\s/g, '')}`} className="text-[hsl(var(--inv-slate))]">{t('contact.phone')}</a></p>
                <p><span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t('contact.addressLabel')}</span><br /><span className="text-muted-foreground">{t('contact.address')}</span></p>
                <p><span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t('contact.hoursLabel')}</span><br /><span className="text-muted-foreground">{t('contact.hours')}</span></p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <Card className="border-border/70 p-6 shadow-[var(--shadow-sm)] sm:p-8">
                <ContactForm />
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
